/* utility for rendering google product schema JSON blobs to improve SEO availability
 * https://developers.google.com/search/docs/data-types/product
 * */
import React from "react";

import {
    IImageURL,
    IProductSKU,
    IProductUPC,
    ISafeHTML,
    IWebPageURL,
} from "../models/nominals";

interface IProps {
    name: string;
    imageUrl?: IImageURL;
    description: ISafeHTML;
    sku: IProductUPC | IProductSKU;
    brandName: string;
    ratingValue: number | null;
    reviewCount: number;
    // Enhanced properties for better SEO
    url?: IWebPageURL | string;
    price?: number | string;
    priceCurrency?: string;
    availability?: "InStock" | "OutOfStock" | "PreOrder";
    category?: string;
}

interface ProductSchema {
    "@context": string;
    "@type": string;
    "name": string;
    "description": ISafeHTML;
    "sku": IProductUPC | IProductSKU;
    "brand": {
        "@type": string;
        "name": string;
    };
    "image"?: IImageURL[];
    "url"?: IWebPageURL | string;
    "aggregateRating"?: {
        "@type": string;
        "ratingValue": number;
        "reviewCount": number;
    };
    "offers"?: {
        "@type": string;
        "price": number | string;
        "priceCurrency": string;
        "availability": string;
        "url"?: IWebPageURL | string;
    };
    "category"?: string;
}

export const ProductSchemaTag = (props: IProps): React.ReactNode => {
    const schema: ProductSchema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": props.name,
        "description": props.description,
        "sku": props.sku,
        "brand": {
            "@type": "Brand",
            "name": props.brandName,
        },
    };

    if (props.imageUrl) {
        schema.image = [props.imageUrl];
    }

    if (props.url) {
        schema.url = props.url;
    }

    if (props.ratingValue && props.reviewCount) {
        schema.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": props.ratingValue,
            "reviewCount": props.reviewCount,
        };
    }

    if (props.price) {
        schema.offers = {
            "@type": "Offer",
            "price": props.price,
            "priceCurrency": props.priceCurrency || "USD",
            "availability": `https://schema.org/${props.availability || "InStock"}`,
        };

        if (props.url) {
            schema.offers.url = props.url;
        }
    }
    if (props.category) {
        schema.category = props.category;
    }

    return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
};
export default ProductSchemaTag;
